import type {
    FilingCheckListResult, FilingCheckRow, FilingCheckSummary,
    FilingCheckFilters, FilingCheckSortField, FilingCheckDocument, FilingCheckLinkResult,
    FilingCheckManualLink,
} from '../types/docuwareFilingCheck';

export interface DocumentSearchParams {
    companyCode: string;
    search?: string;
    amount?: string;
    storedFrom?: string;
    storedTo?: string;
    /** Always returned, whatever the filters say — the preselected candidate must not vanish. */
    pinnedDocumentId?: number | null;
}

interface ListParams {
    page: number;
    limit: number;
    filters: FilingCheckFilters;
    sortBy: FilingCheckSortField;
    sortOrder: 'asc' | 'desc';
}

const EMPTY_SUMMARY: FilingCheckSummary = {
    minPostingDate: '', companies: [], byCompany: [], postedTotals: {},
};

/** Unwrap the house envelope, or throw with the server's own message. */
async function unwrap<T>(res: Response): Promise<{ data: T; total: number }> {
    const body = await res.json();
    if (!res.ok || !body.success) {
        throw new Error(body.error?.message || `API error: ${res.status}`);
    }
    return { data: body.data as T, total: body.pagination?.total ?? 0 };
}

export const docuwareFilingCheckApi = {
    /** Raw fetch, same reason as the other two tabs: the total rides in `pagination`. */
    async list(params: ListParams): Promise<FilingCheckListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        for (const [key, value] of Object.entries(params.filters)) {
            if (value === undefined || value === null || value === '') continue;
            sp.set(key, String(value));
        }

        const res = await fetch(`/api/v1/docuware-invoices/filing-check?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data, total } = await unwrap<{ items: FilingCheckRow[]; summary: FilingCheckSummary }>(res);
        return {
            items: data?.items ?? [],
            summary: { ...EMPTY_SUMMARY, ...(data?.summary ?? {}) },
            total,
        };
    },

    /**
     * Every hand link that exists today.
     *
     * Its own call, not part of the list: a linked posting has LEFT the exception list by
     * design, so without this an undo would only ever reach a link made in the same session.
     */
    async manualLinks(): Promise<FilingCheckManualLink[]> {
        const res = await fetch('/api/v1/docuware-invoices/filing-check/links', {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<{ items: FilingCheckManualLink[] }>(res);
        return data?.items ?? [];
    },

    /** The candidates the link dialog offers. Company-scoped server-side, always. */
    async searchDocuments(params: DocumentSearchParams): Promise<FilingCheckDocument[]> {
        const sp = new URLSearchParams();
        sp.set('companyCode', params.companyCode);
        for (const key of ['search', 'amount', 'storedFrom', 'storedTo'] as const) {
            const value = params[key];
            if (value) sp.set(key, value);
        }
        if (params.pinnedDocumentId) sp.set('pinnedDocumentId', String(params.pinnedDocumentId));

        const res = await fetch(`/api/v1/docuware-invoices/filing-check/documents?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<{ items: FilingCheckDocument[] }>(res);
        return data?.items ?? [];
    },

    /**
     * Link a posting to a document by hand, or take that link away.
     *
     * encodeURIComponent on the posting number is not decoration: dch's series is
     * 'PV-CH2601681' today, and nothing guarantees the next series is path-safe.
     */
    async link(
        companyCode: string, bcDocumentNo: string, documentId: number
    ): Promise<FilingCheckLinkResult> {
        const res = await fetch(
            `/api/v1/docuware-invoices/filing-check/${encodeURIComponent(bcDocumentNo)}/link`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ companyCode, documentId }),
            }
        );
        const { data } = await unwrap<FilingCheckLinkResult>(res);
        return data;
    },

    async unlink(
        companyCode: string, bcDocumentNo: string, documentId: number
    ): Promise<FilingCheckLinkResult> {
        const sp = new URLSearchParams({ companyCode, documentId: String(documentId) });
        const res = await fetch(
            `/api/v1/docuware-invoices/filing-check/${encodeURIComponent(bcDocumentNo)}/link?${sp}`,
            { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
        );
        const { data } = await unwrap<FilingCheckLinkResult>(res);
        return data;
    },
};
